import { useState } from 'react';
import {
  faceNotation,
  opponent,
  squareName,
  type Action,
  type HouseRuleSpec,
  type ItemSpec,
  type MatchView,
  type Side,
} from '../../engine';
import { t } from '../../i18n';
import {
  cardLong,
  faceLong,
  itemName,
  itemText,
  omenName,
  pawnRef,
  statusName,
  type Names,
} from '../../app/describe';
import { Modal } from './Modal';
import { ItemCard, ItemSheet, showCurio, type ShowItem } from './night/Item';
import { NightIcon } from './night/NightIcon';
import { Emblem } from './night/Emblem';
import { Mantel } from './night/Mantel';
import { mantelObject } from './night/Item';
import { CoinStack } from './night/Hud';
import { ruleN } from '../../engine/ledger/state';

/**
 * The Long Night at the table (docs/SHOP_DESIGN.md §8): one small brass pill in the top bar,
 * which opens the Ledger: your Mantel in the order it counts, the Curios you can use, the
 * table's House Rules and Omens, and what the other side holds.
 */

type UseCurio = Extract<Action, { type: 'USE_CURIO' }>;

/** Curios that draw a card: they reveal something, so they are played at once, never drafted. */
export const DRAWING_CURIOS: ReadonlySet<string> = new Set(['pocket-watch', 'spyglass']);

/** A House Rule's text, its number filled in (the rule's own, else the one it plays with). */
export const ruleText = (r: HouseRuleSpec): string =>
  itemText(`rule.${r.id}`, {
    n: ruleN(r) ?? '',
    color: r.color ? t(`color.${r.color}`) : '',
  });

/** The pill in the top bar: brass, and a glow when a Curio can be used now. */
export function LedgerPill({
  v,
  side,
  curioReady,
  onOpen,
}: {
  v: MatchView;
  side: Side;
  curioReady: boolean;
  onOpen: () => void;
}) {
  const l = v.ledger;
  if (!l) return null;
  return (
    <button
      type="button"
      className={`ln-bar-pill ${curioReady ? 'is-ready' : ''}`}
      onClick={onOpen}
      aria-label={t('ledger.openLabel', { n: l.brass[side] })}
      title={t('ledger.title')}
    >
      <span className="hud-tokens" aria-hidden="true">
        <CoinStack />
      </span>
      <span>¤{l.brass[side]}</span>
      {curioReady && <span className="ln-bar-dot" aria-hidden="true" />}
    </button>
  );
}

export const superstitionItem = (item: ItemSpec): ShowItem => ({
  kindLabel: t('night.kind.superstition'),
  name: item.finish ? `${itemName(item.id)} (${itemName(item.finish)})` : itemName(item.id),
  text: [
    itemText(item.id),
    item.finish ? `${itemName(item.finish)}: ${itemText(item.finish)}` : '',
    item.value !== undefined ? t('ledger.value', { n: item.value }) : '',
  ]
    .filter(Boolean)
    .join('\n\n'),
  icon: 'superstition',
  object: mantelObject(item),
});

/**
 * A side's kit at the table, in the order it is shown: its Superstitions in Mantel order, then its
 * Curios. Their sheets step through it in this order.
 */
export function kitItems(v: MatchView, side: Side): { uid: string; show: ShowItem }[] {
  const l = v.ledger;
  if (!l) return [];
  return [
    ...l.mantel[side].map((item) => ({ uid: item.uid, show: superstitionItem(item) })),
    ...l.curios[side].map((item) => ({ uid: item.uid, show: showCurio(item) })),
  ];
}

/** Everything the Long Night puts on this table, for both sides. */
export function LedgerDialog({
  v,
  viewer,
  names,
  sideName,
  curioActions,
  curioHint,
  onCurio,
  onClose,
}: {
  v: MatchView;
  viewer: Side;
  names: Names;
  /** A side as the strips name it: "You", "The Clockmaker". */
  sideName: (side: Side) => string;
  /** Legal USE_CURIO actions for the viewer now. */
  curioActions: readonly UseCurio[];
  /** Why a Curio that could be used is not offered now (a drawing one mid-turn). */
  curioHint: (item: ItemSpec) => string | null;
  onCurio: (item: ItemSpec) => void;
  onClose: () => void;
}) {
  // A thing open in its sheet, and the side's kit it steps through.
  const [open, setOpen] = useState<{ side: Side; uid: string } | null>(null);
  const l = v.ledger;
  const cfg = v.config.ledger;
  if (!l || !cfg) return null;
  const other = opponent(viewer);
  const usable = new Set(
    curioActions.map((a) => l.curios[viewer].find((c) => c.uid === a.uid)?.id ?? ''),
  );
  const omens = l.omens.filter((o) => !o.spent);
  const statuses = Object.entries(l.statuses).filter(([, list]) => list.length > 0);
  const mantel = (side: Side) => (
    <Mantel
      items={l.mantel[side]}
      slots={cfg.kits[side].slots ?? 5}
      size="sheet"
      onOpen={(item) => setOpen({ side, uid: item.uid })}
    />
  );
  return (
    <Modal title={t('ledger.title')} onClose={onClose} wide className="ln-inventory">
      <div className="ln-ledger-brass">
        {([viewer, other] as const).map((side) => (
          <span key={side}>
            <span className="ln-eyebrow">{sideName(side)}</span>
            <strong className="ln-gold ln-with-icon">
              <NightIcon name="brass" size={18} />
              {l.brass[side]}
            </strong>
          </span>
        ))}
      </div>

      <div className="ln-section-head">
        <h3 className="ln-eyebrow">{t('ledger.yourMantel')}</h3>
        <span className="ln-hint">{t('night.mantelHint')}</span>
      </div>
      {mantel(viewer)}

      {l.curios[viewer].length > 0 && (
        <>
          <div className="ln-section-head">
            <h3 className="ln-eyebrow">{t('ledger.curios')}</h3>
            <span className="ln-hint">{t('ledger.curiosHint')}</span>
          </div>
          <div className="ln-grid">
            {l.curios[viewer].map((item) => {
              const can = usable.has(item.id) && curioHint(item) === null;
              return (
                <div key={item.uid} className="ln-curio">
                  <ItemCard
                    item={showCurio(item)}
                    onClick={() => setOpen({ side: viewer, uid: item.uid })}
                  />
                  <button
                    type="button"
                    className={`btn btn-small ${can ? 'btn-primary' : ''}`}
                    disabled={!can}
                    title={curioHint(item) ?? undefined}
                    onClick={() => onCurio(item)}
                  >
                    {t('night.use')}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {(cfg.houseRules.length > 0 || omens.length > 0) && (
        <>
          <h3 className="ln-eyebrow">{t('ledger.thisTable')}</h3>
          <ul className="ln-list">
            {cfg.houseRules.map((r, i) => (
              <li key={`${r.id}-${i}`}>
                <strong>{itemName(`rule.${r.id}`)}</strong>{' '}
                <span className="ln-level">
                  {t('ledger.against', { side: sideName(r.side ?? 'S') })}
                </span>
                <span className="ln-list-text">{ruleText(r)}</span>
              </li>
            ))}
            {omens.map((o) => (
              <li key={o.id}>
                <strong className="ln-with-icon">
                  <Emblem name={o.kind === 'unknown' ? 'unknown' : `omen.${o.kind}`} size={20} />
                  {omenName(o.kind)} · {squareName(o.square)}
                </strong>
                <span className="ln-list-text">{itemText(`omen.${o.kind}`)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {statuses.length > 0 && (
        <>
          <h3 className="ln-eyebrow">{t('ledger.statuses')}</h3>
          <ul className="ln-list">
            {statuses.map(([pawnId, list]) => (
              <li key={pawnId}>
                <strong>{pawnRef(v, pawnId, names)}</strong>
                {list.map((st) => (
                  <span key={st.kind} className="ln-list-text">
                    {statusName(st.kind)}: {itemText(`status.${st.kind}`)}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </>
      )}

      {l.mantel[other].length > 0 && (
        <>
          <div className="ln-section-head">
            <h3 className="ln-eyebrow">{t('ledger.theirMantel', { name: sideName(other) })}</h3>
          </div>
          {mantel(other)}
        </>
      )}

      {open &&
        (() => {
          const kit = kitItems(v, open.side);
          const at = kit.findIndex((k) => k.uid === open.uid);
          const shown = kit[at];
          if (!shown) return null;
          return (
            <ItemSheet
              item={shown.show}
              actions={[]}
              browse={
                kit.length > 1
                  ? {
                      at,
                      of: kit.length,
                      onStep: (d) =>
                        setOpen({
                          side: open.side,
                          uid: kit[(at + d + kit.length) % kit.length]!.uid,
                        }),
                    }
                  : undefined
              }
              onClose={() => setOpen(null)}
            />
          );
        })()}
    </Modal>
  );
}

/** What a Curio is used on: one button per legal way of using it. */
export function CurioDialog({
  v,
  names,
  item,
  actions,
  onPick,
  onClose,
}: {
  v: MatchView;
  names: Names;
  item: ItemSpec;
  /** Legal USE_CURIO actions for this Curio's kind. */
  actions: readonly UseCurio[];
  onPick: (a: UseCurio) => void;
  onClose: () => void;
}) {
  const label = (a: UseCurio): string => {
    const tg = a.target;
    if (!tg) return t('ledger.curio.use');
    if (tg.face) return faceLong(tg.face);
    if (tg.cardId) return t('ledger.curio.swap', { card: cardLong(v.cards[tg.cardId]) });
    if (tg.epitaphId && tg.pawnId)
      return t('ledger.curio.replayWith', { pawn: pawnRef(v, tg.pawnId, names) });
    if (tg.epitaphId) {
      const theirs = v.players[opponent(a.side)].epitaphs.find((e) => e.id === tg.epitaphId);
      return t('ledger.curio.copyEpitaph', {
        pawn: theirs ? names.pawn(theirs.sourcePawnId) : tg.epitaphId,
      });
    }
    if (tg.pawnId && tg.to)
      return t('ledger.curio.pull', {
        pawn: pawnRef(v, tg.pawnId, names),
        square: squareName(tg.to),
      });
    if (tg.pawnId) {
      const p = v.pawns[tg.pawnId];
      return p?.square
        ? `${pawnRef(v, tg.pawnId, names)} · ${squareName(p.square)}`
        : pawnRef(v, tg.pawnId, names);
    }
    return t('ledger.curio.use');
  };
  return (
    <Modal
      title={itemName(item.id)}
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={onClose}>
          {t('action.cancel')}
        </button>
      }
    >
      <p className="ln-sheet-text">{itemText(item.id)}</p>
      <p className="ln-hint">{t('ledger.curio.choose')}</p>
      <div className="curio-options">
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            className={`btn ${a.target?.face ? `card-face face-${a.target.face.color}` : ''}`}
            data-autofocus={i === 0 ? true : undefined}
            onClick={() => onPick(a)}
          >
            {a.target?.face ? faceNotation(a.target.face) : label(a)}
          </button>
        ))}
      </div>
    </Modal>
  );
}
